import type { ColumnaConfigItem, ColumnasConfig } from '@/hooks/useColumnasConfig';
import { ArrowCounterClockwise, DotsSixVertical, SlidersHorizontal } from '@phosphor-icons/react';
import { type DragEvent, type ReactElement, useEffect, useRef, useState } from 'react';

/**
 * Gestor de columnas reutilizable: botón "Columnas" que abre un popover con la
 * lista de columnas del reporte. Permite:
 *  - Ocultar/mostrar (checkbox).
 *  - Reordenar (arrastrar por el asa ⠿).
 *  - Restablecer al orden/visibilidad por defecto.
 *
 * El ordenamiento por columna (clic en el encabezado) lo maneja la tabla; acá
 * solo se gestionan orden y visibilidad. Trabaja contra el estado devuelto por
 * `useColumnasConfig`.
 */
export function GestorColumnas({
  columnas,
  config,
}: {
  columnas: ColumnaConfigItem[];
  config: ColumnasConfig;
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const etiquetaDe = new Map(columnas.map((c) => [c.id, c.etiqueta]));
  const ocultasCount = config.orden.filter((id) => config.ocultas.has(id)).length;

  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [abierto]);

  const onDrop = (destinoId: string) => (e: DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (id) config.mover(id, destinoId);
    setArrastrando(null);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 font-medium text-zinc-600 hover:bg-zinc-100"
        title="Mostrar/ocultar y reordenar columnas"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" weight="bold" />
        Columnas
        {ocultasCount > 0 && (
          <span className="rounded-full bg-zinc-200 px-1.5 text-[10px] text-zinc-600">
            {ocultasCount} oculta{ocultasCount === 1 ? '' : 's'}
          </span>
        )}
      </button>
      {abierto && (
        <div className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-zinc-100 border-b px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Columnas
            </span>
            <button
              type="button"
              onClick={config.reset}
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
              title="Restablecer orden y visibilidad"
            >
              <ArrowCounterClockwise className="h-3 w-3" weight="bold" />
              Restablecer
            </button>
          </div>
          <ul className="max-h-72 overflow-y-auto p-1">
            {config.orden.map((id) => {
              const visible = !config.ocultas.has(id);
              return (
                <li
                  key={id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', id);
                    e.dataTransfer.effectAllowed = 'move';
                    setArrastrando(id);
                  }}
                  onDragEnd={() => setArrastrando(null)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={onDrop(id)}
                  className={`flex items-center gap-2 rounded px-1.5 py-1 text-xs ${
                    arrastrando === id ? 'opacity-40' : 'hover:bg-zinc-50'
                  }`}
                >
                  <span className="cursor-grab text-zinc-400" title="Arrastrar para reordenar">
                    <DotsSixVertical className="h-4 w-4" weight="bold" />
                  </span>
                  <label className="flex flex-1 cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={visible} onChange={() => config.toggle(id)} />
                    <span className={visible ? '' : 'text-zinc-400 line-through'}>
                      {etiquetaDe.get(id) ?? id}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
